import { CONVERSATION_LIST_SECTION_ROW_KEY } from './conversationListPresentation';

const hasValue = (value) =>
  value !== undefined && value !== null && value !== '';

const BULK_SELECTION_SECTION_ROW_KEY = CONVERSATION_LIST_SECTION_ROW_KEY;

const normalizeId = (id) => (hasValue(id) ? String(id) : null);

const isSelectableConversation = (item) =>
  Boolean(item && !item[BULK_SELECTION_SECTION_ROW_KEY] && hasValue(item.id));

const getSelectableConversations = (list = []) =>
  (Array.isArray(list) ? list : []).filter(isSelectableConversation);

const resolveSelectedConversations = (selectedIds = [], lists = []) => {
  const byId = new Map();

  (Array.isArray(lists) ? lists : []).forEach((list) => {
    getSelectableConversations(list).forEach((conversation) => {
      const key = normalizeId(conversation.id);
      if (key && !byId.has(key)) {
        byId.set(key, conversation);
      }
    });
  });

  return (Array.isArray(selectedIds) ? selectedIds : [])
    .map((id) => byId.get(normalizeId(id)))
    .filter(Boolean);
};

const getConversationIds = (conversations = []) =>
  getSelectableConversations(conversations).map(
    (conversation) => conversation.id
  );

const getUniqueInboxIds = (conversations = []) => {
  const ids = getSelectableConversations(conversations)
    .map((conversation) => conversation.inbox_id)
    .filter(hasValue);
  return [...new Set(ids)];
};

const getRemainingSelectedIds = (selectedIds = [], idsToRemove = []) => {
  const removeIds = Array.isArray(idsToRemove) ? idsToRemove : [idsToRemove];
  const removeSet = new Set(removeIds.map(normalizeId).filter(Boolean));

  return (Array.isArray(selectedIds) ? selectedIds : []).filter(
    (id) => !removeSet.has(normalizeId(id))
  );
};

const selectedConversationsHaveStatus = ({
  selectedIds = [],
  resolvedConversations = [],
  getConversationById,
  status,
} = {}) => {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  if (!ids.length || !status) {
    return false;
  }

  const resolvedById = new Map(
    getSelectableConversations(resolvedConversations).map((conversation) => [
      normalizeId(conversation.id),
      conversation,
    ])
  );

  return ids.every((id) => {
    const conversation =
      resolvedById.get(normalizeId(id)) ||
      (typeof getConversationById === 'function'
        ? getConversationById(id)
        : null);
    return conversation?.status === status;
  });
};

const areAllVisibleConversationsSelected = (
  visibleConversations = [],
  selectedIds = []
) => {
  const visibleIds = getConversationIds(visibleConversations);
  if (!visibleIds.length) {
    return false;
  }

  const selected = new Set(
    (Array.isArray(selectedIds) ? selectedIds : []).map(normalizeId)
  );
  return visibleIds.every((id) => selected.has(normalizeId(id)));
};

const isConversationIdSelected = (selectedIds, id = null) => {
  const normalizedId = normalizeId(id);
  if (!normalizedId) {
    return false;
  }

  return (Array.isArray(selectedIds) ? selectedIds : []).some(
    (selectedId) => normalizeId(selectedId) === normalizedId
  );
};

export {
  areAllVisibleConversationsSelected,
  BULK_SELECTION_SECTION_ROW_KEY,
  getConversationIds,
  getRemainingSelectedIds,
  getSelectableConversations,
  getUniqueInboxIds,
  isConversationIdSelected,
  resolveSelectedConversations,
  selectedConversationsHaveStatus,
};
